export function cleanText(value: unknown): string {
  return String(value ?? "")
    .replace(/^\uFEFF/, "")
    .trim();
}

export function fileKey(file: File): string {
  const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  return `${path}|${file.size}|${file.lastModified}`;
}

export function normalizeHeader(value: unknown): string {
  return cleanText(value)
    .replace(/^"+|"+$/g, "")
    .replace(/^\?"+|"+$/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = cleanText(value);
  if (!text) return 0;
  const isParen = /^\(.*\)$/.test(text);
  const cleaned = text
    .replace(/[,$¥￥%]/g, "")
    .replace(/[()]/g, "")
    .replace(/\s+/g, "");
  const num = Number.parseFloat(cleaned);
  if (!Number.isFinite(num)) return 0;
  return isParen ? -num : num;
}

export function toInt(value: unknown): number {
  return Math.trunc(toNumber(value));
}

export function excelSerialToDate(serial: number): Date {
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);
  const fractionalDay = serial - Math.floor(serial) + 0.0000001;
  const totalSeconds = Math.floor(86400 * fractionalDay);
  dateInfo.setSeconds(totalSeconds);
  return dateInfo;
}

export function ymd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function mdyLabel(date: string): string {
  const d = parseLocalDate(date);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

export function parseLocalDate(date: string): Date {
  const [year, month, day] = date.split("-").map((v) => Number.parseInt(v, 10));
  return new Date(year, month - 1, day);
}

export function addDays(date: string, days: number): string {
  const d = parseLocalDate(date);
  d.setDate(d.getDate() + days);
  return ymd(d);
}

export function daysBetween(start: string, end: string): number {
  const ms = parseLocalDate(end).getTime() - parseLocalDate(start).getTime();
  return Math.round(ms / 86400000);
}

export function monthKey(date: string): string {
  return date.slice(0, 7);
}

export function daysInMonth(month: string): number {
  const [year, mon] = month.split("-").map((v) => Number.parseInt(v, 10));
  return new Date(year, mon, 0).getDate();
}

export function monthLabel(date: string): string {
  const [year, mon] = monthKey(date).split("-");
  return `${year}-${mon}`;
}

export function weekInterval(date: string): string {
  const d = parseLocalDate(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  const start = ymd(d);
  d.setDate(d.getDate() + 6);
  return `${mdyLabel(start)}-${mdyLabel(ymd(d))}`;
}

export function formatUtcInTimeZone(value: unknown, timezone: string): string {
  const date = parseDateValue(value);
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "number" && Number.isFinite(value)) return excelSerialToDate(value);
  const text = cleanText(value);
  if (!text) return null;
  if (/^\d+(\.\d+)?$/.test(text)) return excelSerialToDate(Number.parseFloat(text));
  const direct = new Date(text);
  if (!Number.isNaN(direct.getTime())) return direct;
  const normalized = text.replace(/\b(PDT|PST|UTC|GMT)\b/g, "").trim();
  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
}

export function parseReportDate(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return ymd(excelSerialToDate(value));
  if (value instanceof Date && !Number.isNaN(value.getTime())) return ymd(value);
  const text = cleanText(value);
  if (!text) return "";
  const iso = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const amazon = text.match(/^([A-Za-z]{3,})\s+(\d{1,2}),\s+(\d{4})/);
  if (amazon) {
    const months: Record<string, number> = {
      jan: 1,
      january: 1,
      feb: 2,
      february: 2,
      mar: 3,
      march: 3,
      apr: 4,
      april: 4,
      may: 5,
      jun: 6,
      june: 6,
      jul: 7,
      july: 7,
      aug: 8,
      august: 8,
      sep: 9,
      sept: 9,
      september: 9,
      oct: 10,
      october: 10,
      nov: 11,
      november: 11,
      dec: 12,
      december: 12
    };
    const month = months[amazon[1].toLowerCase()];
    if (month) {
      return `${amazon[3]}-${String(month).padStart(2, "0")}-${amazon[2].padStart(2, "0")}`;
    }
  }
  const parsed = parseDateValue(value);
  return parsed ? ymd(parsed) : "";
}

export function uniqueSortedDates(dates: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(dates).filter(Boolean))).sort();
}

export function safeDivide(numerator: number, denominator: number): number {
  return denominator ? numerator / denominator : 0;
}

export function round(value: number, decimals = 6): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function sum<T>(rows: T[], getter: (row: T) => number): number {
  return rows.reduce((acc, row) => acc + getter(row), 0);
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
